import { IonIcon } from '@ionic/react';
import { addOutline, chatbubbleOutline, closeOutline } from 'ionicons/icons';
import React, { useEffect, useRef } from 'react';
import { AgentRun } from './AgentChat.types';

/**
 * One tab per open conversation, so a DJ can leave an agent working and ask
 * something else meanwhile. Hidden until a conversation exists, which keeps the
 * rail looking the way it did before tabs for anyone asking a single question.
 *
 * The rail is narrow and there is no cap on open conversations, so the strip
 * scrolls sideways and pulls the active tab back into view when it changes.
 */

const isBusy = (run: AgentRun): boolean =>
  run.status === 'running' || run.status === 'queued';

export const AgentChatTabs: React.FC<{
  tabs: AgentRun[];
  activeId: string | null;
  onSelect: (runId: string) => void;
  onClose: (runId: string) => void;
  onNewChat: () => void;
  disabled?: boolean;
}> = ({ tabs, activeId, onSelect, onClose, onNewChat, disabled = false }) => {
  const activeRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // 'nearest' on both axes so pulling a tab into view never scrolls the
    // thread underneath it.
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeId, tabs.length]);

  if (tabs.length === 0) return null;

  return (
    <div className="agent-chat-tabs">
      <div
        className="agent-chat-tab-list"
        role="tablist"
        aria-label="Conversations"
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          return (
            <span
              key={tab.id}
              ref={active ? activeRef : undefined}
              className={`agent-chat-tab${active ? ' is-active' : ''}`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={active}
                className="agent-chat-tab-label"
                title={tab.prompt}
                onClick={() => onSelect(tab.id)}
              >
                <IonIcon
                  icon={chatbubbleOutline}
                  className="agent-chat-tab-icon"
                  aria-hidden="true"
                />
                {isBusy(tab) && (
                  <i
                    className="agent-chat-tab-dot"
                    role="img"
                    aria-label="Working"
                  />
                )}
                <span className="agent-chat-tab-title">{tab.prompt}</span>
              </button>
              <button
                type="button"
                className="agent-chat-tab-close"
                aria-label={`Close ${tab.prompt}`}
                onClick={() => onClose(tab.id)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </span>
          );
        })}
      </div>
      <button
        type="button"
        className="agent-chat-new-icon"
        disabled={disabled}
        aria-label="New chat"
        onClick={onNewChat}
      >
        <IonIcon icon={addOutline} aria-hidden="true" />
      </button>
    </div>
  );
};

export default AgentChatTabs;
